import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  Chip,
  Box,
  Divider,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ArrowLeft, ArrowRight, Eye, Filter, FilterX, RefreshCw, X } from 'lucide-react';
import { messagesApi } from '../../../api';
import { API_BASE_URL } from '../../../utils/constants';
import { useUIStore } from '../../../store';

const MessagesPage = ({
  sess = { uid: '', role: 'user' },
}) => {
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [whatsappNumbers, setWhatsappNumbers] = useState([]);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [mediaContent, setMediaContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ type: '', message: '' });
  const showSnackbar = useUIStore((state) => state.showSnackbar);

  const rowsPerPage = 10;
  const userRole = sess.role;

  const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp']);
  const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'ogg', '3gp', 'mov', 'mkv']);
  const AUDIO_EXTENSIONS = new Set(['mp3', 'wav', 'ogg', 'm4a', 'aac', 'opus']);
  const DOCUMENT_EXTENSIONS = new Set(['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt']);
  const normalizeBaseUrl = (value) => String(value || '').replace(/\/+$/, '');

  useEffect(() => {
    loadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status.type !== 'success' || !status.message) return;
    showSnackbar({ message: status.message, severity: 'success' });
    setStatus({ type: '', message: '' });
  }, [showSnackbar, status]);

  const getFileExtension = (value) => {
    if (!value) return '';
    const clean = String(value).split('?')[0].split('#')[0];
    const lastDot = clean.lastIndexOf('.');
    if (lastDot === -1) return '';
    return clean.slice(lastDot + 1).toLowerCase();
  };

  const getFileNameFromUrl = (value) => {
    if (!value) return '';
    const clean = String(value).split('?')[0].split('#')[0];
    const parts = clean.split('/');
    return parts[parts.length - 1] || '';
  };

  const hasKnownExtension = (value) => {
    const ext = getFileExtension(value);
    if (!ext) return false;
    return (
      IMAGE_EXTENSIONS.has(ext) ||
      VIDEO_EXTENSIONS.has(ext) ||
      AUDIO_EXTENSIONS.has(ext) ||
      DOCUMENT_EXTENSIONS.has(ext)
    );
  };

  const isLikelyUrl = (value) => {
    if (!value) return false;
    const normalized = String(value).trim();
    if (!normalized) return false;
    if (/^(https?:\/\/|data:|blob:)/i.test(normalized)) return true;
    if (
      normalized.includes('/uploads/') ||
      normalized.includes('/mediaFile') ||
      normalized.includes('mediaFile') ||
      normalized.includes('/media/')
    ) {
      return true;
    }
    if (normalized.startsWith('/')) return hasKnownExtension(normalized);
    return hasKnownExtension(normalized);
  };

  const resolveMediaUrl = (value) => {
    const normalized = String(value || '').trim();
    if (!normalized) return '';
    if (/^(https?:\/\/|data:|blob:)/i.test(normalized)) return normalized;
    const base = normalizeBaseUrl(API_BASE_URL || '');
    const cleaned = normalized.replace(/^\/+/, '');
    if (!base) return `/${cleaned}`;
    return `${base}/${cleaned}`;
  };

  const findFirstUrl = (value, depth = 0, seen = new WeakSet()) => {
    if (!value || depth > 2) return '';
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (isLikelyUrl(trimmed)) return trimmed;
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        try {
          return findFirstUrl(JSON.parse(trimmed), depth + 1, seen);
        } catch (error) {
          return '';
        }
      }
      return '';
    }
    if (Array.isArray(value)) {
      for (const entry of value) {
        const found = findFirstUrl(entry, depth + 1, seen);
        if (found) return found;
      }
      return '';
    }
    if (typeof value === 'object') {
      if (seen.has(value)) return '';
      seen.add(value);
      for (const entry of Object.values(value)) {
        const found = findFirstUrl(entry, depth + 1, seen);
        if (found) return found;
      }
    }
    return '';
  };

  const inferMediaCategory = (mimeType, type, url) => {
    const normalizedType = String(type || '').toLowerCase();
    const normalizedMime = String(mimeType || '').toLowerCase();
    if (['image', 'video', 'audio', 'document'].includes(normalizedType)) return normalizedType;
    if (normalizedMime.startsWith('image/')) return 'image';
    if (normalizedMime.startsWith('video/')) return 'video';
    if (normalizedMime.startsWith('audio/')) return 'audio';
    if (normalizedMime.includes('pdf') || normalizedMime.includes('word') || normalizedMime.includes('excel')) {
      return 'document';
    }
    const ext = getFileExtension(url);
    if (IMAGE_EXTENSIONS.has(ext)) return 'image';
    if (VIDEO_EXTENSIONS.has(ext)) return 'video';
    if (AUDIO_EXTENSIONS.has(ext)) return 'audio';
    if (DOCUMENT_EXTENSIONS.has(ext)) return 'document';
    if (ext) return 'document';
    return 'document';
  };

  const resolveMediaPreview = (message) => {
    if (!message) return null;
    const rawMedia = message.media || null;
    let mediaObj = null;
    let url = '';
    let mimeType = '';
    let fileName = '';
    let typeHint = message.type || message.mediaType || message.media_type || '';

    if (rawMedia && typeof rawMedia === 'object') {
      mediaObj = rawMedia;
    } else if (typeof rawMedia === 'string') {
      const trimmed = rawMedia.trim();
      if (trimmed) {
        if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
          try {
            mediaObj = JSON.parse(trimmed);
          } catch (error) {
            url = trimmed;
          }
        } else {
          url = trimmed;
        }
      }
    }

    if (Array.isArray(mediaObj)) {
      mediaObj = mediaObj[0];
    }

    if (mediaObj && typeof mediaObj === 'object') {
      url =
        mediaObj.url ||
        mediaObj.link ||
        mediaObj.href ||
        mediaObj.media_url ||
        mediaObj.mediaUrl ||
        mediaObj.file ||
        mediaObj.file_url ||
        mediaObj.fileUrl ||
        url;
      mimeType = mediaObj.mimeType || mediaObj.contentType || mediaObj.mime_type || '';
      fileName = mediaObj.file_name || mediaObj.fileName || mediaObj.filename || mediaObj.name || '';
      typeHint = mediaObj.type || mediaObj.media_type || typeHint;
    }

    if (!url) {
      const fallbackUrl = findFirstUrl(message);
      if (fallbackUrl) {
        url = fallbackUrl;
      }
    }

    if (!url && message.raw) {
      const fallbackUrl = findFirstUrl(message.raw);
      if (fallbackUrl) {
        url = fallbackUrl;
      }
    }

    if (!url) {
      const candidateText = message.text || message.message || message.content || '';
      if (isLikelyUrl(candidateText)) {
        url = candidateText.trim();
      }
    }

    if (!url || !isLikelyUrl(url)) return null;

    const resolvedUrl = resolveMediaUrl(url);
    const category = inferMediaCategory(mimeType, typeHint, resolvedUrl);
    const extension = getFileExtension(resolvedUrl);
    const resolvedFileName = fileName || getFileNameFromUrl(resolvedUrl) || 'Media file';
    const isPdf = extension === 'pdf' || mimeType === 'application/pdf';

    return {
      url: resolvedUrl,
      mimeType,
      category,
      fileName: resolvedFileName,
      isPdf,
    };
  };

  const buildMediaContent = (preview) => {
    if (!preview?.url) return null;
    if (preview.category === 'image') {
      return (
        <Box component="img" src={preview.url} alt={preview.fileName || 'Image'} sx={{ maxWidth: '100%', borderRadius: 1 }} />
      );
    }
    if (preview.category === 'video') {
      return (
        <Box component="video" controls sx={{ maxWidth: '100%', borderRadius: 1 }}>
          <source src={preview.url} type={preview.mimeType || 'video/mp4'} />
          Your browser does not support the video tag.
        </Box>
      );
    }
    if (preview.category === 'audio') {
      return (
        <audio controls style={{ width: '100%' }}>
          <source src={preview.url} type={preview.mimeType || 'audio/mpeg'} />
          Your browser does not support the audio element.
        </audio>
      );
    }
    if (preview.isPdf) {
      return (
        <Box
          component="iframe"
          title={preview.fileName || 'Document'}
          src={preview.url}
          sx={{ width: '100%', minHeight: 520, border: 'none', borderRadius: 1 }}
        />
      );
    }

    return (
      <Typography>
        Document attached.{` `}
        <Box
          component="a"
          href={preview.url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'primary.main' }}
        >
          Click here to view or download
        </Box>
        .
      </Typography>
    );
  };

  const normalizeMessage = (item) => {
    let media = item.media || null;
    if (!media && item.md_allmsg) {
      try {
        const parsed = JSON.parse(item.md_allmsg);
        media = parsed.image || parsed.video || parsed.audio || parsed.document;
      } catch (e) {
        media = null;
      }
    }

    return {
      id: item.id || item.md_id || item.messageId,
      customerNumber: item.customerNumber || item.md_cusnumber || item.to,
      userNumber: item.userNumber || item.md_usernumber || item.from,
      text: item.message || item.md_msgtext || item.text,
      type: item.type || item.md_msgtype || (media ? media.type : 'text'),
      direction: item.direction || item.md_msgdiraction || (item.incoming ? 'IN' : 'OUT'),
      status: item.status || item.md_msgstatus,
      updatedAt: item.updatedAt || item.createdAt || item.md_updated,
      error: item.error || item.md_msgerror,
      category: item.category || item.md_msgcategory,
      media,
      raw: item,
    };
  };

  const loadMessages = async (filters = {}) => {
    try {
      setLoading(true);
      const response = await messagesApi.getAll(filters);
      // API response structure: { success, message, data: { data: [...], pagination: {...} } }
      const payload = response?.data?.data || response?.data || {};
      const list = Array.isArray(payload) ? payload : (payload?.data || []);
      const normalized = list.map(normalizeMessage);
      setAllData(normalized);
      setFilteredData(normalized);
      const numbers = new Set();
      normalized.forEach((msg) => {
        if (msg.customerNumber) numbers.add(msg.customerNumber);
        if (msg.userNumber && userRole === 'superadmin') numbers.add(msg.userNumber);
      });
      setWhatsappNumbers(Array.from(numbers));
      setStatus({ type: '', message: '' });
    } catch (error) {
      console.error(error);
      const message = error?.response?.data?.message || 'Failed to load messages';
      setStatus({ type: 'error', message });
      setAllData([]);
      setFilteredData([]);
      setWhatsappNumbers([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '---';
    const date = new Date(dateString);
    return date
      .toLocaleString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Asia/Kolkata',
      })
      .replace(/,/g, '');
  };

  const handleFilterChange = (type, value) => {
    const newFilters = { ...selectedFilters };
    if (value) {
      if (type === 'number') {
        if (userRole === 'superadmin') newFilters.userNumber = value;
        newFilters.customerNumber = value;
      } else if (type === 'status') {
        newFilters.status = value;
      } else if (type === 'category') {
        newFilters.category = value;
      } else if (type === 'direction') {
        newFilters.direction = value;
      } else if (type === 'messageType') {
        newFilters.messageType = value;
        newFilters.type = value;
      } else if (type === 'fromDate') {
        newFilters.fromDate = value;
      } else if (type === 'toDate') {
        newFilters.toDate = value;
      }
    } else {
      if (type === 'number') {
        delete newFilters.customerNumber;
        delete newFilters.userNumber;
      } else if (type === 'status') {
        delete newFilters.status;
      } else if (type === 'category') {
        delete newFilters.category;
      } else if (type === 'direction') {
        delete newFilters.direction;
      } else if (type === 'messageType') {
        delete newFilters.messageType;
        delete newFilters.type;
      } else if (type === 'fromDate') {
        delete newFilters.fromDate;
      } else if (type === 'toDate') {
        delete newFilters.toDate;
      }
    }
    setSelectedFilters(newFilters);
    setCurrentPage(1);
    loadMessages(newFilters);
  };

  const clearAllFilters = () => {
    setSelectedFilters({});
    setCurrentPage(1);
    loadMessages({});
  };

  const fetchAndShowMedia = (message) => {
    const preview = resolveMediaPreview(message);
    if (!preview) {
      setStatus({ type: 'error', message: 'No media attached to this message.' });
      return;
    }

    setMediaContent(buildMediaContent(preview));
    setShowMediaModal(true);
  };

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  const headers = userRole === 'superadmin'
    ? ['S.No', 'Whatsapp', 'Customer', 'Message', 'Type', 'Direction', 'Status', 'Date', 'Error']
    : ['S.No', 'Number', 'Message', 'Type', 'Direction', 'Status', 'Date', 'Error'];

  const renderStatusChip = (status) => {
    const value = (status || '').toString().toUpperCase();
    return (
      <Chip
        label={value || 'N/A'}
        size="small"
        color={
          value === 'SENT' ? 'info' :
          value === 'DELIVERED' ? 'success' :
          value === 'READ' ? 'primary' :
          value === 'FAILED' ? 'error' : 'default'
        }
        variant="outlined"
        sx={{ textTransform: 'capitalize' }}
      />
    );
  };

  const renderDirectionChip = (direction) => {
    const value = (direction || '').toString().toUpperCase();
    const isOut = value === 'OUT';
    return (
      <Chip
        label={isOut ? 'OUT' : 'IN'}
        size="small"
        color={isOut ? 'primary' : 'success'}
        icon={isOut ? <ArrowRight size={12} /> : <ArrowLeft size={12} />}
        variant="outlined"
      />
    );
  };

  const activeFilterCount = Object.keys(selectedFilters).length;
  const selectMenuProps = {
    PaperProps: {
      sx: {
        mt: 0.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        backgroundImage: 'none',
        boxShadow: 6,
        overflow: 'hidden',
        '& .MuiMenuItem-root': {
          minHeight: 42,
          backgroundColor: 'background.paper',
        },
      },
    },
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h5" fontWeight={600}>
            Messages
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshCw size={16} />}
            onClick={() => loadMessages(selectedFilters)}
          >
            Refresh
          </Button>
        </Stack>

        {/* Filters Card */}
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Filter size={18} />
                <Typography variant="subtitle1" fontWeight={500}>
                  Filters
                </Typography>
                {activeFilterCount > 0 && (
                  <Chip
                    label={activeFilterCount}
                    size="small"
                    color="primary"
                    sx={{ height: 20, fontSize: '0.75rem' }}
                  />
                )}
              </Stack>
              {activeFilterCount > 0 && (
                <Button
                  size="small"
                  color="error"
                  startIcon={<FilterX size={14} />}
                  onClick={clearAllFilters}
                  sx={{ textTransform: 'none' }}
                >
                  Clear All
                </Button>
              )}
            </Stack>
          </Box>

          <CardContent sx={{ pt: 2 }}>
            <Stack spacing={2.5}>
              {/* Row 1: Primary Filters */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ flexWrap: 'wrap' }}
              >
                <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
                  <InputLabel>WhatsApp Number</InputLabel>
                  <Select
                    label="WhatsApp Number"
                    value={selectedFilters.customerNumber || ''}
                    onChange={(e) => handleFilterChange('number', e.target.value)}
                    MenuProps={selectMenuProps}
                  >
                    <MenuItem value="">All Numbers</MenuItem>
                    {whatsappNumbers.map((num, idx) => (
                      <MenuItem key={idx} value={num}>{num}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 140, flex: 1 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={selectedFilters.status || ''}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    MenuProps={selectMenuProps}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="SENT">Sent</MenuItem>
                    <MenuItem value="DELIVERED">Delivered</MenuItem>
                    <MenuItem value="FAILED">Failed</MenuItem>
                    <MenuItem value="READ">Read</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 130, flex: 1 }}>
                  <InputLabel>Direction</InputLabel>
                  <Select
                    label="Direction"
                    value={selectedFilters.direction || ''}
                    onChange={(e) => handleFilterChange('direction', e.target.value)}
                    MenuProps={selectMenuProps}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="IN">Incoming</MenuItem>
                    <MenuItem value="OUT">Outgoing</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 140, flex: 1 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    label="Type"
                    value={selectedFilters.messageType || ''}
                    onChange={(e) => handleFilterChange('messageType', e.target.value)}
                    MenuProps={selectMenuProps}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="text">Text</MenuItem>
                    <MenuItem value="image">Image</MenuItem>
                    <MenuItem value="video">Video</MenuItem>
                    <MenuItem value="audio">Audio</MenuItem>
                    <MenuItem value="document">Document</MenuItem>
                    <MenuItem value="location">Location</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              {/* Row 2: Secondary Filters */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ flexWrap: 'wrap' }}
              >
                <FormControl size="small" sx={{ minWidth: 140, flex: 1, maxWidth: { sm: 200 } }}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    label="Category"
                    value={selectedFilters.category || ''}
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                    MenuProps={selectMenuProps}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="service">Service</MenuItem>
                    <MenuItem value="business">Business</MenuItem>
                  </Select>
                </FormControl>

                <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />

                <Stack direction="row" spacing={2} sx={{ flex: 1 }}>
                  <TextField
                    size="small"
                    type="date"
                    label="From Date"
                    InputLabelProps={{ shrink: true }}
                    value={selectedFilters.fromDate || ''}
                    onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                    sx={{ flex: 1, maxWidth: 180 }}
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="To Date"
                    InputLabelProps={{ shrink: true }}
                    value={selectedFilters.toDate || ''}
                    onChange={(e) => handleFilterChange('toDate', e.target.value)}
                    sx={{ flex: 1, maxWidth: 180 }}
                  />
                </Stack>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
          <CardContent>
            {status.message && status.type === 'error' && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {status.message}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {headers.map((header, idx) => (
                          <TableCell key={idx}>{header}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={headers.length}>
                            <Typography align="center" color="text.secondary">
                              No data available
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        currentData.map((item, index) => {
                          const formattedMsgText = item?.text
                            ? item.text
                                .split(' ')
                                .map((word) => (word.length > 30 ? `${word.substring(0, 30)}...` : word))
                                .join(' ')
                            : '';
                          const preview = resolveMediaPreview(item);
                          const showThumbnail = preview?.category === 'image';

                          return (
                            <TableRow key={item.id || index} hover>
                              <TableCell>{startIndex + index + 1}</TableCell>
                              {userRole === 'superadmin' && (
                                <TableCell>{item.userNumber || '-'}</TableCell>
                              )}
                              <TableCell>{item.customerNumber || '-'}</TableCell>
                              <TableCell title={item.text}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  {showThumbnail && (
                                    <Box
                                      component="img"
                                      src={preview.url}
                                      alt={preview.fileName || 'Image'}
                                      onClick={() => fetchAndShowMedia(item)}
                                      sx={{
                                        width: 42,
                                        height: 42,
                                        borderRadius: 1,
                                        objectFit: 'cover',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        cursor: 'pointer',
                                      }}
                                    />
                                  )}
                                  {formattedMsgText ? (
                                    <Typography>{formattedMsgText}</Typography>
                                  ) : preview ? (
                                    <Typography variant="caption" color="text.secondary">
                                      [{preview.category || 'media'}]
                                    </Typography>
                                  ) : null}
                                  {preview && (
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={() => fetchAndShowMedia(item)}
                                      startIcon={<Eye size={14} />}
                                    >
                                      View
                                    </Button>
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ textTransform: 'capitalize' }}>{item.type || 'text'}</TableCell>
                              <TableCell>{renderDirectionChip(item.direction)}</TableCell>
                              <TableCell>{renderStatusChip(item.status)}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(item?.updatedAt)}</TableCell>
                              <TableCell>{item.error || '---'}</TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {totalPages > 1 && (
                  <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} mt={2}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <Typography variant="body2">Page {currentPage} of {totalPages}</Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </Stack>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <Dialog
          open={showMediaModal}
          onClose={() => setShowMediaModal(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Media Preview</Typography>
            <IconButton onClick={() => setShowMediaModal(false)}>
              <X size={16} />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {mediaContent}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={() => setShowMediaModal(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default MessagesPage;
